import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Mail, ExternalLink, FileText, Twitter, GraduationCap, Copy, Check } from 'lucide-react';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [showGDDSCitation, setShowGDDSCitation] = useState(false);
  const [showFineTuningCitation, setShowFineTuningCitation] = useState(false);
  const [showLLMCitation, setShowLLMCitation] = useState(false);
  const [copiedGDDS, setCopiedGDDS] = useState(false);
  const [copiedFineTuning, setCopiedFineTuning] = useState(false);
  const [copiedLLM, setCopiedLLM] = useState(false);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <section className="text-center mb-16 animate-fade-in-up">
        <div className="mb-8">
          {/* Profile Picture and Name */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden shadow-2xl border-4 border-white/20 mb-6 animate-fade-in-up animation-delay-100">
              <img 
                src="/profile_pic_cropped.jpg" 
                alt="Oussama Zekri" 
                className="w-full h-full object-cover object-center"
                style={{
                  imageRendering: 'auto'
                }}
              />
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-semibold mb-4 text-stone-800">
              {t('home.title')}
            </h1>
            <div className="w-24 h-px bg-stone-400 mx-auto mb-6 animate-slide-in"></div>
          </div>
          
          <p className="text-xl md:text-2xl text-stone-700 font-light leading-relaxed max-w-3xl mx-auto">
            <Trans
              i18nKey="home.subtitleWithGroup"
              components={{
                groupLink: (
                  <a
                    href="https://akorba.github.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300"
                  />
                )
              }}
            />
          </p>

          <div className="mt-10 grid items-start gap-5 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] animate-fade-in-up animation-delay-200">
            <div className="relative overflow-hidden rounded-[2rem] border border-stone-200/80 bg-[linear-gradient(145deg,rgba(255,251,246,0.98),rgba(244,239,233,0.92))] p-7 text-left shadow-[0_24px_70px_rgba(31,41,55,0.10)] md:p-8">
              <div className="absolute -right-10 top-0 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(120,113,108,0.22),rgba(120,113,108,0))] blur-2xl animate-float-drift"></div>
              <div className="absolute -left-6 bottom-0 h-28 w-28 rounded-full bg-[radial-gradient(circle,rgba(163,138,112,0.18),rgba(163,138,112,0))] blur-2xl animate-float-drift animation-delay-300"></div>
              <p className="relative mb-4 text-xs uppercase tracking-[0.24em] text-stone-500">
                {t('home.snapshot.eyebrow')}
              </p>
              <p className="relative max-w-2xl text-base leading-relaxed text-stone-700 md:text-lg">
                {t('home.snapshot.description')}
              </p>
              <p className="relative mt-4 max-w-2xl text-base leading-relaxed text-stone-700">
                {t('home.advisorInfo')}{' '}
                <a href="https://akorba.github.io/" target="_blank" rel="noopener noreferrer" 
                   className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                  {t('home.people.annaKorba')}
                </a>{' '}
                and{' '}
                <a href="https://nboulle.github.io/" target="_blank" rel="noopener noreferrer" 
                   className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                  {t('home.people.nicolasBoulle')}
                </a>.
              </p>

            </div>

            <div className="rounded-[1.75rem] border border-stone-200/80 bg-[rgba(255,252,248,0.9)] p-6 text-left shadow-[0_18px_45px_rgba(31,41,55,0.07)]">
              <p className="mb-3 text-xs uppercase tracking-[0.22em] text-stone-500">
                {t('home.snapshot.latest')}
              </p>
              <div className="mb-2 text-sm font-medium text-stone-500">
                {t('home.snapshot.latestDate')}
              </div>
              <Link
                to="/gdds"
                className="mb-3 block font-display text-2xl font-semibold text-stone-800 transition-colors duration-300 hover:text-stone-600"
              >
                {t('publications.papers.gdds.title')}
              </Link>
              <p className="mb-5 text-sm leading-relaxed text-stone-600">
                {t('home.snapshot.latestDescription')}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://arxiv.org/abs/2603.21342"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-full border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition-all duration-300 hover:bg-stone-100"
                >
                  {t('home.snapshot.latestLink')}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
                <Link
                  to="/gdds"
                  className="inline-flex items-center rounded-full border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition-all duration-300 hover:bg-stone-100"
                >
                  {t('publications.projectPage')}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Selected Publications Section */}
      <section className="mb-16 animate-fade-in-up animation-delay-300">
        <div className="bg-[rgba(255,251,246,0.82)] backdrop-blur-sm rounded-[1.75rem] p-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)] border border-stone-200/80">
          <h2 className="font-display text-4xl font-semibold text-stone-800 mb-8 text-center">{t('home.selectedPublications')}</h2>
          <div className="space-y-6">
            <div className="rounded-[1.5rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(255,252,248,0.96),rgba(244,239,233,0.88))] p-6 shadow-[0_16px_40px_rgba(31,41,55,0.06)] transition-all duration-300 group hover:shadow-[0_18px_48px_rgba(31,41,55,0.08)]">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="w-full md:w-80 h-48 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-black">
                    <video
                      className="w-full h-full object-contain md:object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    >
                      <source src="/files/publications/gdds/videos/gdds_forward_noising_teaser.mp4" type="video/mp4" />
                      Your browser does not support the teaser video.
                    </video>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    to="/gdds"
                    className="font-display text-2xl font-semibold text-stone-800 transition-colors duration-300 group-hover:text-stone-600"
                  >
                    {t('publications.papers.gdds.title')}
                  </Link>
                  <p className="text-stone-700 mb-3 text-sm">
                    <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.gdds.authors')}
                  </p>
                  <p className="text-stone-600 text-sm mb-3">
                    {t('publications.papers.gdds.description')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a href="https://arxiv.org/abs/2603.21342" target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Paper
                    </a>
                    <Link
                      to="/gdds"
                      className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      {t('publications.projectPage')}
                    </Link>
                    <a href="https://github.com/ozekri/gdds" target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100">
                      <Github className="w-4 h-4 mr-1" />
                      Code
                    </a>
                    <button
                      onClick={() => setShowGDDSCitation(!showGDDSCitation)}
                      className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Cite
                    </button>
                  </div>

                  {showGDDSCitation && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`@article{zekri2026generalized,
  title={Generalized Discrete Diffusion from Snapshots},
  author={Zekri, Oussama and Uscidda, Th{\\'e}o and Boull{\\'e}, Nicolas and Korba, Anna},
  journal={arXiv preprint arXiv:2603.21342},
  year={2026}
}`);
                            setCopiedGDDS(true);
                            setTimeout(() => setCopiedGDDS(false), 2000);
                          }}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors duration-200"
                        >
                          {copiedGDDS ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          {copiedGDDS ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{zekri2026generalized,
  title={Generalized Discrete Diffusion from Snapshots},
  author={Zekri, Oussama and Uscidda, Th{\\'e}o and Boull{\\'e}, Nicolas and Korba, Anna},
  journal={arXiv preprint arXiv:2603.21342},
  year={2026}
}`}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Fine-Tuning Discrete Diffusion Models */}
            <div className="rounded-[1.5rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(255,252,248,0.96),rgba(244,239,233,0.88))] p-6 shadow-[0_16px_40px_rgba(31,41,55,0.06)] transition-all duration-300 group hover:shadow-[0_18px_48px_rgba(31,41,55,0.08)]">
              <div className="flex flex-col md:flex-row gap-6">
                {/* GIF Image */}
                <div className="flex-shrink-0">
                  <div className="w-full md:w-80 h-48 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-white">
                    <img 
                      src="/files/publications/gifs_and_imgs/sepo.gif" 
                      alt="Fine-tuning discrete diffusion models with policy gradient methods visualization" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <a href="https://arxiv.org/abs/2502.01384" target="_blank" rel="noopener noreferrer" 
                     className="font-display text-2xl font-semibold text-stone-800 transition-colors duration-300 group-hover:text-stone-600">
                    {t('publications.papers.fineTuning.title')}
                  </a>
                  <p className="text-stone-700 mb-3 text-sm">
                    <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.fineTuning.authors')}
                  </p>
                  <p className="text-stone-600 text-sm mb-3">
                    {t('publications.papers.fineTuning.description')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a href="https://arxiv.org/abs/2502.01384" target="_blank" rel="noopener noreferrer" 
                       className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Paper
                    </a>
                    <a href="https://github.com/ozekri/SEPO" target="_blank" rel="noopener noreferrer" 
                       className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100">
                      <Github className="w-4 h-4 mr-1" />
                      Code
                    </a>
                    <button 
                      onClick={() => setShowFineTuningCitation(!showFineTuningCitation)}
                      className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Cite
                    </button>
                  </div>
                  
                  {showFineTuningCitation && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`@article{zekri2025fine,
  title={Fine-tuning discrete diffusion models with policy gradient methods},
  author={Zekri, Oussama and Boull{\\'e}, Nicolas},
  journal={arXiv preprint arXiv:2502.01384},
  year={2025}
}`);
                            setCopiedFineTuning(true);
                            setTimeout(() => setCopiedFineTuning(false), 2000);
                          }}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors duration-200"
                        >
                          {copiedFineTuning ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          {copiedFineTuning ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{zekri2025fine,
  title={Fine-tuning discrete diffusion models with policy gradient methods},
  author={Zekri, Oussama and Boull{\\'e}, Nicolas},
  journal={arXiv preprint arXiv:2502.01384},
  year={2025}
}`}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Large Language Models as Markov Chains */}
            <div className="rounded-[1.5rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(255,252,248,0.96),rgba(244,239,233,0.88))] p-6 shadow-[0_16px_40px_rgba(31,41,55,0.06)] transition-all duration-300 group hover:shadow-[0_18px_48px_rgba(31,41,55,0.08)]">
              <div className="flex flex-col md:flex-row gap-6">
                {/* GIF Image */}
                <div className="flex-shrink-0">
                  <div className="w-full md:w-80 h-48 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <img 
                      src="/assets/llm_as_mc_toy.gif" 
                      alt="Large Language Models as Markov Chains visualization" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <a href="https://arxiv.org/abs/2410.02724" target="_blank" rel="noopener noreferrer" 
                     className="font-display text-2xl font-semibold text-stone-800 transition-colors duration-300 group-hover:text-stone-600">
                    {t('publications.papers.llmMarkov.title')}
                  </a>
                  <p className="text-stone-700 mb-3 text-sm">
                    <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.llmMarkov.authors')}
                  </p>
                  <p className="text-stone-600 text-sm mb-3">
                    {t('publications.papers.llmMarkov.description')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a href="https://arxiv.org/abs/2410.02724" target="_blank" rel="noopener noreferrer" 
                       className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Paper
                    </a>
                    <button 
                      onClick={() => setShowLLMCitation(!showLLMCitation)}
                      className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-sm text-stone-700 transition-colors duration-200 hover:bg-stone-100"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Cite
                    </button>
                  </div>
                  
                  {showLLMCitation && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`@article{zekri2024large,
  title={Large language models as markov chains},
  author={Zekri, Oussama and Odonnat, Ambroise and Benechehab, Abdelhakim and Bleistein, Linus and Boull{\\'e}, Nicolas and Redko, Ievgen},
  journal={arXiv preprint arXiv:2410.02724},
  year={2024}
}`);
                            setCopiedLLM(true);
                            setTimeout(() => setCopiedLLM(false), 2000);
                          }}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors duration-200"
                        >
                          {copiedLLM ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          {copiedLLM ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{zekri2024large,
  title={Large language models as markov chains},
  author={Zekri, Oussama and Odonnat, Ambroise and Benechehab, Abdelhakim and Bleistein, Linus and Boull{\\'e}, Nicolas and Redko, Ievgen},
  journal={arXiv preprint arXiv:2410.02724},
  year={2024}
}`}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="mb-16 animate-fade-in-up animation-delay-400">
        <div className="bg-[rgba(255,251,246,0.82)] backdrop-blur-sm rounded-[1.75rem] p-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)] border border-stone-200/80">
          <h2 className="font-display text-4xl font-semibold text-stone-800 mb-8 text-center">{t('home.latestNews')}</h2>
          <div className="space-y-6">
            <div className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50/50 transition-all duration-300 group">
              <div className="w-3 h-3 bg-stone-500 rounded-full mt-2 flex-shrink-0 animate-pulse"></div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 font-medium">(23/03/2026)</span>
                  <span className="text-xs bg-stone-700 text-white px-2 py-1 rounded-full font-medium">{t('home.newPreprint')}</span>
                </div>
                <p className="text-gray-700 mt-1">
                  <Link
                    to="/gdds"
                    className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300 group-hover:text-stone-800"
                  >
                    {t('publications.papers.gdds.title')}
                  </Link>{' '}
                  {t('home.news.gdds.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50/50 transition-all duration-300 group">
              <div className="w-3 h-3 bg-[#6f7f5f] rounded-full mt-2 flex-shrink-0 animate-pulse"></div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 font-medium">(18/09/2025)</span>
                  <span className="text-xs bg-[#ece3da] text-[#6f6257] px-2 py-1 rounded-full font-medium">NeurIPS 2025</span>
                </div>
                <p className="text-gray-700 mt-1">
                  <a href="https://arxiv.org/abs/2502.01384" target="_blank" rel="noopener noreferrer" 
                     className="text-[#5f6d52] hover:text-[#49543f] font-medium underline decoration-[#a9b49f] decoration-1 underline-offset-4 transition-all duration-300 group-hover:text-[#49543f]">
                    Fine-Tuning Discrete Diffusion Models with Policy Gradient Methods
                  </a>{' '}
                  {t('home.news.fineTuning.description')}
                </p>
              </div>
            </div>

            {/* <div className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50/50 transition-all duration-300 group">
              <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 flex-shrink-0 animate-pulse"></div>
              <div>
                <span className="text-sm text-gray-500 font-medium">(18/06/2024)</span>
                <p className="text-gray-700 mt-1">
                  Workshop paper accepted at{' '}
                  <a href="https://icml.cc/2024/" target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:text-blue-800 font-medium underline decoration-2 underline-offset-4 transition-all duration-300 hover:decoration-blue-400 group-hover:text-blue-700">
                    ICML 2024
                  </a>{' '}
                  (1st Workshop on In-Context Learning)!
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Previous Experience */}
      <section className="mb-16 animate-fade-in-up animation-delay-500">
        <div className="rounded-[1.75rem] border border-stone-200/80 bg-[rgba(255,251,246,0.82)] p-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
          <h2 className="font-display text-4xl font-semibold text-stone-800 mb-6">{t('home.previousExperience')}</h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="space-y-4 text-stone-700">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-stone-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <a href="https://www.imperial.ac.uk/mathematics/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.institutions.imperial')}
                  </a>{' '}
                  ({t('home.institutions.with')}{' '}
                  <a href="https://nboulle.github.io/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.nicolasBoulle')}
                  </a>) - {t('home.researchAreas.imperial')}
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-stone-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <a href="https://gtsbrain-paris.github.io/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.institutions.huawei')}
                  </a>{' '}
                  ({t('home.institutions.with')}{' '}
                  <a href="https://ievred.github.io/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.ievgenRedko')}
                  </a>) - {t('home.researchAreas.huawei')}
                </div>
              </div>
            </div>
            <div className="space-y-4 text-stone-700">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-stone-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <a href="https://www.kyoto-u.ac.jp/en" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.institutions.kyoto')}
                  </a>{' '}
                  ({t('home.institutions.with')}{' '}
                  <a href="http://www-optima.amp.i.kyoto-u.ac.jp/staff_website/ellen/index.html" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.ellenFukuda')}
                  </a>,{' '}
                  <a href="https://bflourenco.github.io/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.brunoLourenco')}
                  </a>,{' '}
                  <a href="https://sites.google.com/view/tianxiangmath" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.tianxiangLiu')}
                  </a>) - {t('home.researchAreas.kyoto')}
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-stone-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <a href="https://www.centreborelli.fr/en" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 font-medium underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.institutions.borelli')}
                  </a>{' '}
                  ({t('home.institutions.with')}{' '}
                  <a href="http://www.laurentoudre.fr/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.laurentOudre')}
                  </a>,{' '}
                  <a href="https://fr.linkedin.com/in/thibaut-germain" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-700 hover:text-stone-900 underline decoration-stone-400 decoration-1 underline-offset-4 transition-all duration-300">
                    {t('home.people.thibautGermain')}
                  </a>) - {t('home.researchAreas.borelli')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Links Section */}
      <section className="text-center animate-fade-in-up animation-delay-600">
        <div className="rounded-[1.75rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(255,251,246,0.95),rgba(243,239,231,0.92))] p-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
          <h2 className="font-display text-4xl font-semibold text-stone-800 mb-6">{t('home.connectExplore')}</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://logb-research.github.io/" target="_blank" rel="noopener noreferrer" 
               className="inline-flex items-center rounded-full border border-stone-200 bg-white/90 px-6 py-3 font-medium text-stone-700 transition-all duration-300 hover:bg-stone-100">
              <span>{t('home.researchBlog')}</span>
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
            
            <a href="/files/general/CV_ZEKRI_Oussama.pdf" target="_blank" rel="noopener noreferrer" 
               className="inline-flex items-center rounded-full border border-stone-200 bg-white/90 px-6 py-3 font-medium text-stone-700 transition-all duration-300 hover:bg-stone-100">
              <span>{t('home.resumeCV')}</span>
              <FileText className="w-4 h-4 ml-2" />
            </a>

            <a href="mailto:oussama.zekri@ens-paris-saclay.fr" 
               className="inline-flex items-center rounded-full border border-stone-200 bg-white/90 px-6 py-3 font-medium text-stone-700 transition-all duration-300 hover:bg-stone-100">
              <span>{t('common.email')}</span>
              <Mail className="w-4 h-4 ml-2" />
            </a>
          </div>

          <div className="flex justify-center space-x-6 mt-8">
            <a href="https://scholar.google.com/citations?user=E9EqupAAAAAJ" target="_blank" rel="noopener noreferrer" 
               className="p-2 text-stone-500 transition-colors duration-300 rounded-lg hover:bg-stone-100 hover:text-stone-800">
              <span className="sr-only">Google Scholar</span>
              <GraduationCap className="w-12 h-12" />
            </a>
            
            <a href="https://github.com/ozekri" target="_blank" rel="noopener noreferrer" 
               className="p-2 text-stone-500 transition-colors duration-300 rounded-lg hover:bg-stone-100 hover:text-stone-800">
              <span className="sr-only">GitHub</span>
              <Github className="w-12 h-12" />
            </a>
            
            <a href="https://linkedin.com/in/ozekri" target="_blank" rel="noopener noreferrer" 
               className="p-2 text-stone-500 transition-colors duration-300 rounded-lg hover:bg-stone-100 hover:text-stone-800">
              <span className="sr-only">LinkedIn</span>
              <Linkedin className="w-12 h-12" />
            </a>

            <a href="https://twitter.com/oussamazekri_" target="_blank" rel="noopener noreferrer" 
               className="p-2 text-stone-500 transition-colors duration-300 rounded-lg hover:bg-stone-100 hover:text-stone-800">
              <span className="sr-only">Twitter/X</span>
              <Twitter className="w-12 h-12" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 
